import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { formatApiError } from "@/lib/api";
import {
  Building2, CheckCircle2, ShieldCheck, ArrowRight, Lock, Mail, Phone, Globe,
  MapPin, FileText, CalendarCheck, Send, Quote,
} from "lucide-react";
import { alpha, mix, readableOn, themeOf, heroSrcOf, safeAccent, PLAIN_HERO } from "@/lib/portalTheme";
import useWhiteLabelHead from "@/lib/useWhiteLabelHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const LABEL = "text-[10.5px] font-bold uppercase tracking-[0.16em]";

const Mark = ({ portal, size = 38, accent }) =>
  portal.logo
    ? <img src={portal.logo} alt="" style={{ height: size, maxWidth: size * 3.4, objectFit: "contain" }} data-testid="portal-logo" />
    : <span className="rounded-2xl flex items-center justify-center shrink-0" style={{ width: size, height: size, background: alpha(accent, 0.14), color: accent }}>
        <Building2 size={size * 0.5} />
      </span>;

/* ---------- sections ---------- */
const Highlights = ({ items, t, accent }) => {
  if (!items.length) return null;
  return (
    <div className="max-w-[1120px] mx-auto px-6 md:px-10">
      <div className="flex flex-wrap gap-3" data-testid="portal-trust-highlights">
        {items.map((h, i) => (
          <div key={h} className="flex-1 min-w-[220px] rounded-2xl border px-5 py-4 flex items-start gap-2.5 fade-up"
            style={{ background: t.card, borderColor: t.border, animationDelay: `${0.05 * i}s` }}>
            <CheckCircle2 size={15} className="shrink-0 mt-0.5" style={{ color: accent }} />
            <span className="text-[13px] font-medium leading-snug" style={{ color: t.ink }}>{h}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const HowItWorks = ({ t, accent, name }) => {
  const steps = [
    { icon: Send, title: "Send your request", body: `Pick a plan, tell us the amount you have in mind, and submit. Nothing moves and nothing is committed.` },
    { icon: FileText, title: "We review and confirm", body: `${name} checks the request, talks it through with you, and confirms the funding details by email.` },
    { icon: CalendarCheck, title: "Statements every month", body: "Once funded you get a private statement link — capital, the return accrued, what's been paid, and the maturity date." },
  ];
  return (
    <div className="max-w-[1120px] mx-auto px-6 md:px-10" data-testid="portal-how-it-works">
      <div className={`${LABEL} mb-5`} style={{ color: t.ink3 }}>How it works</div>
      <div className="grid md:grid-cols-3 gap-4">
        {steps.map((s, i) => (
          <div key={s.title} className="rounded-2xl p-6 fade-up" style={{ background: t.card, border: `1px solid ${t.border}`, animationDelay: `${0.06 * i}s` }}>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: alpha(accent, 0.13), color: accent }}>
                <s.icon size={16} />
              </span>
              <span className="font-mono text-[11px] tracking-widest" style={{ color: t.ink3 }}>{String(i + 1).padStart(2, "0")}</span>
            </div>
            <div className="font-display text-lg font-semibold tracking-tight" style={{ color: t.ink }}>{s.title}</div>
            <p className="text-[13px] leading-relaxed mt-1.5" style={{ color: t.ink2 }}>{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const About = ({ portal, t, accent }) => {
  const hasText = portal.about_text || portal.about_title;
  const hasAdviser = portal.adviser_name || portal.adviser_photo;
  if (!hasText && !hasAdviser) return null;
  return (
    <div className="max-w-[1120px] mx-auto px-6 md:px-10" data-testid="portal-adviser-block">
      <div className={`rounded-3xl overflow-hidden grid ${portal.adviser_photo ? "md:grid-cols-[minmax(0,1fr)_320px]" : ""}`} style={{ background: t.card, border: `1px solid ${t.border}` }}>
        <div className="p-7 md:p-9">
          <div className={`${LABEL} mb-4`} style={{ color: t.ink3 }}>{portal.about_title || `About ${portal.name}`}</div>
          {portal.about_text && (
            <div className="relative">
              <Quote size={26} style={{ color: alpha(accent, 0.28) }} className="mb-2" />
              <p className="text-[15px] leading-[1.75] whitespace-pre-line" style={{ color: t.ink2 }}>{portal.about_text}</p>
            </div>
          )}
          {portal.adviser_name && (
            <div className="mt-6 pt-5 flex items-center gap-3" style={{ borderTop: `1px solid ${t.border}` }}>
              {portal.adviser_photo && <img src={portal.adviser_photo} alt="" className="w-11 h-11 rounded-full object-cover md:hidden" />}
              <div>
                <div className="font-display font-semibold tracking-tight" style={{ color: t.ink }}>{portal.adviser_name}</div>
                {portal.adviser_title && <div className="text-[12px]" style={{ color: t.ink3 }}>{portal.adviser_title}</div>}
              </div>
            </div>
          )}
        </div>
        {portal.adviser_photo && (
          <div className="hidden md:block relative" style={{ minHeight: 280 }}>
            <img src={portal.adviser_photo} alt={portal.adviser_name || ""} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${t.card} 0%, rgba(0,0,0,0) 38%)` }} />
          </div>
        )}
      </div>
    </div>
  );
};

const Footer = ({ portal, t, accent }) => (
  <footer className="max-w-[1120px] mx-auto px-6 md:px-10 pt-4" data-testid="portal-footer">
    <div className="rounded-3xl p-7 md:p-9" style={{ background: t.band, border: `1px solid ${t.border}` }}>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="flex items-center gap-3">
            <Mark portal={portal} size={32} accent={accent} />
            <span className="font-display text-lg font-semibold tracking-tight" style={{ color: t.ink }}>{portal.name}</span>
          </div>
          {portal.address && (
            <div className="flex items-start gap-2 mt-4 text-[12.5px]" style={{ color: t.ink2 }}>
              <MapPin size={13} className="mt-0.5 shrink-0" style={{ color: t.ink3 }} /> <span className="whitespace-pre-line">{portal.address}</span>
            </div>
          )}
        </div>
        <div className="space-y-2 text-[12.5px]">
          {portal.contact_email && (
            <a href={`mailto:${portal.contact_email}`} className="flex items-center gap-2 hover:underline" style={{ color: t.ink2 }} data-testid="portal-contact-email">
              <Mail size={13} style={{ color: accent }} /> {portal.contact_email}
            </a>
          )}
          {portal.contact_phone && (
            <a href={`tel:${portal.contact_phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-2 hover:underline" style={{ color: t.ink2 }} data-testid="portal-contact-phone">
              <Phone size={13} style={{ color: accent }} /> {portal.contact_phone}
            </a>
          )}
          {portal.website && (
            <a href={portal.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline" style={{ color: t.ink2 }} data-testid="portal-website">
              <Globe size={13} style={{ color: accent }} /> {portal.website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </div>
      <div className="mt-7 pt-5 text-[11.5px] leading-relaxed" style={{ borderTop: `1px solid ${t.border}`, color: t.ink3 }}>
        {portal.footer_note && <p className="mb-2 whitespace-pre-line">{portal.footer_note}</p>}
        <p>© {new Date().getFullYear()} {portal.name}. Returns are not guaranteed and capital is at risk. Nothing here is financial advice.</p>
      </div>
    </div>
  </footer>
);

/* ---------- page ---------- */
export default function PartnerPortal() {
  const { slug } = useParams();
  const [portal, setPortal] = useState(null);
  const [gone, setGone] = useState(false);
  const [offerId, setOfferId] = useState("");
  const [values, setValues] = useState({});
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/partner/${slug}`)
      .then(({ data }) => { setPortal(data); setOfferId(data.offers?.[0]?.id || ""); })
      .catch(() => setGone(true));
  }, [slug]);

  const offer = useMemo(() => (portal?.offers || []).find((o) => o.id === offerId), [portal, offerId]);
  useWhiteLabelHead(portal);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    const core = ["name", "email", "phone", "country"];
    const extra = Object.fromEntries(Object.entries(values).filter(([k]) => !core.includes(k)));
    try {
      const { data } = await axios.post(`${API}/public/partner/${slug}/apply`, {
        offer_id: offerId, name: values.name, email: values.email, phone: values.phone,
        country: values.country, amount: Number(amount), extra,
      });
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (gone) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="portal-not-found">
        <div className="glass-strong p-10 text-center max-w-md">
          <Building2 size={26} className="mx-auto mb-3 text-ink-3" />
          <div className="font-display text-xl font-semibold">This portal isn't available</div>
          <p className="text-sm text-ink-2 mt-2">Check the link you were given, or ask your adviser for a fresh one.</p>
        </div>
      </div>
    );
  }
  if (!portal) return <div className="min-h-screen flex items-center justify-center text-sm text-ink-2" data-testid="portal-loading">Loading…</div>;

  const accent = safeAccent(portal.accent || "#4060FF", portal.theme);
  const t = themeOf(portal.theme);
  const onAccent = readableOn(accent);
  const layout = portal.hero_layout || "image";
  const heroSrc = heroSrcOf(portal);
  const showImage = layout !== "plain" && !!heroSrc;
  const headline = portal.headline || `Managed digital-asset income, arranged by ${portal.name}.`;
  const cta = portal.cta_label || "Request your place";
  const goToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const heroText = layout === "image" && showImage ? t.ink : layout === "plain" ? onAccent : t.ink;
  const fieldStyle = { background: t.inputBg, border: `1.5px solid ${t.border}`, color: t.ink };

  return (
    <div className="min-h-screen" style={{ background: t.pageBg, color: t.ink }} data-testid="partner-portal-page">
      {/* header */}
      <header className="sticky top-0 z-30" style={{ background: t.headerBg, backdropFilter: "blur(14px)", borderBottom: `1px solid ${t.border}` }}>
        <div className="max-w-[1120px] mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Mark portal={portal} size={34} accent={accent} />
            <span className="font-display text-[17px] font-semibold tracking-tight truncate" style={{ color: t.ink }} data-testid="portal-name">{portal.name}</span>
          </div>
          {!done && (
            <div className="flex items-center gap-2 shrink-0">
              <a href={`/p/${slug}/login`} className="rounded-full px-3.5 py-2 text-[12.5px] font-semibold transition-colors"
                style={{ color: t.ink2, border: `1px solid ${t.border}` }} data-testid="portal-client-login">
                Client sign-in
              </a>
              <button onClick={goToForm} className="rounded-full px-4 py-2 text-[12.5px] font-semibold transition-transform hover:-translate-y-0.5"
                style={{ background: accent, color: onAccent, boxShadow: `0 8px 22px ${alpha(accent, 0.34)}` }} data-testid="portal-header-cta">
                {cta}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden" data-testid="portal-hero">
        {showImage && layout === "image" && (
          <>
            <img src={heroSrc} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: t.scrim }} />
          </>
        )}
        {layout === "plain" && (
          <div className="absolute inset-0" style={{ background: PLAIN_HERO(accent) }} />
        )}
        <div className={`relative max-w-[1120px] mx-auto px-6 md:px-10 ${layout === "split" ? "py-12 md:py-14" : "py-16 md:py-24"}`}>
          <div className={layout === "split" ? "grid md:grid-cols-2 gap-10 items-center" : ""}>
            <div className={layout === "split" ? "" : "max-w-[640px]"}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11.5px] font-semibold mb-6 fade-up"
                style={{ background: layout === "plain" ? "rgba(255,255,255,0.18)" : alpha(accent, 0.12), color: layout === "plain" ? onAccent : accent }}>
                <ShieldCheck size={13} /> Managed programme · applications reviewed individually
              </div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-[44px] font-extrabold leading-[1.08] tracking-tight fade-up"
                style={{ color: heroText, animationDelay: "0.05s" }} data-testid="portal-headline">
                {headline}
              </h1>
              {portal.tagline && (
                <p className="text-base sm:text-lg leading-relaxed mt-4 max-w-[560px] fade-up"
                  style={{ color: layout === "plain" ? alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.86) : t.ink2, animationDelay: "0.1s" }}
                  data-testid="portal-tagline">
                  {portal.tagline}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-8 fade-up" style={{ animationDelay: "0.15s" }}>
                <button onClick={goToForm} className="rounded-full px-6 py-3 text-[13.5px] font-bold inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
                  style={{ background: layout === "plain" ? onAccent : accent, color: layout === "plain" ? accent : onAccent, boxShadow: `0 12px 30px ${alpha(accent, 0.3)}` }}
                  data-testid="portal-hero-cta">
                  {cta} <ArrowRight size={15} />
                </button>
                {!!portal.offers.length && (
                  <span className="text-[12.5px] font-medium" style={{ color: layout === "plain" ? alpha(onAccent === "#FFFFFF" ? "#FFFFFF" : "#0F1226", 0.8) : t.ink3 }}>
                    {portal.offers.length} plan{portal.offers.length > 1 ? "s" : ""} open · from {usd(Math.min(...portal.offers.map((o) => o.min_ticket)))}
                  </span>
                )}
              </div>
            </div>
            {layout === "split" && showImage && (
              <div className="rounded-3xl overflow-hidden fade-up" style={{ border: `1px solid ${t.border}`, boxShadow: `0 30px 60px ${alpha(accent, 0.18)}`, animationDelay: "0.1s" }}>
                <img src={heroSrc} alt="" className="w-full h-[280px] md:h-[340px] object-cover" />
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="space-y-14 md:space-y-20 pb-16 -mt-6 relative z-10">
        <Highlights items={portal.highlights || []} t={t} accent={accent} />

        {done ? (
          <div className="max-w-[1120px] mx-auto px-6 md:px-10">
            <div className="rounded-3xl p-9 md:p-12 max-w-[640px] fade-up" style={{ background: t.cardStrong, border: `1px solid ${t.border}` }} data-testid="portal-success">
              <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: alpha(accent, 0.14), color: accent }}>
                <CheckCircle2 size={24} />
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-5" style={{ color: t.ink }}>You're on the list</h2>
              <p className="text-[15px] leading-relaxed mt-3" style={{ color: t.ink2 }}>{done.next}</p>
              <div className="mt-6 rounded-2xl px-5 py-4" style={{ background: t.band, border: `1px solid ${t.border}` }}>
                <div className={LABEL} style={{ color: t.ink3 }}>Your reference</div>
                <div className="font-mono text-lg font-semibold mt-1" style={{ color: accent }} data-testid="portal-reference">{done.reference}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-[1120px] mx-auto px-6 md:px-10">
            <div className="grid lg:grid-cols-[1.05fr_1fr] gap-6 items-start">
              {/* plans */}
              <div>
                <div className={`${LABEL} mb-4`} style={{ color: t.ink3 }}>Choose a plan</div>
                {!portal.offers.length && (
                  <div className="rounded-2xl p-6 text-[13.5px]" style={{ background: t.card, border: `1px solid ${t.border}`, color: t.ink2 }} data-testid="portal-no-offers">
                    No plans are open right now — please check back, or contact us directly.
                  </div>
                )}
                <div className="space-y-3">
                  {portal.offers.map((o, i) => {
                    const on = o.id === offerId;
                    return (
                      <button key={o.id} type="button" onClick={() => setOfferId(o.id)} data-testid={`portal-offer-${o.id}`}
                        className="w-full text-left rounded-2xl p-5 transition-all hover:-translate-y-0.5 fade-up"
                        style={{
                          background: on ? t.cardStrong : t.card,
                          border: `2px solid ${on ? accent : "transparent"}`,
                          boxShadow: on ? `0 16px 40px ${alpha(accent, 0.22)}` : `0 1px 0 ${t.border}`,
                          outline: on ? "none" : `1px solid ${t.border}`,
                          animationDelay: `${0.05 * i}s`,
                        }}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="font-display text-lg font-bold tracking-tight" style={{ color: t.ink }}>{o.name}</div>
                            <div className="flex flex-wrap gap-1.5 mt-2.5">
                              {[`${o.term_months} months`,
                                o.payout_mode === "monthly" ? "Paid monthly" : "Paid at maturity",
                                `${usd(o.min_ticket)}–${usd(o.max_ticket)}`].map((chip) => (
                                <span key={chip} className="px-2.5 py-1 rounded-lg text-[11.5px] font-medium"
                                  style={{ background: t.band, color: t.ink2, border: `1px solid ${t.border}` }}>{chip}</span>
                              ))}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-display text-3xl sm:text-[34px] font-extrabold tracking-tight tabular-nums leading-none" style={{ color: accent }}>
                              {o.promised_rate_pct}%
                            </div>
                            <div className="text-[11px] font-semibold mt-1" style={{ color: t.ink3 }}>per month</div>
                          </div>
                        </div>
                        {on && (
                          <div className="flex items-center gap-1.5 mt-4 text-[11.5px] font-bold" style={{ color: accent }}>
                            <CheckCircle2 size={13} /> Selected
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="rounded-2xl p-5 mt-3 flex items-start gap-3" style={{ background: t.band, border: `1px solid ${t.border}` }} data-testid="portal-security-notice">
                  <Lock size={15} className="mt-0.5 shrink-0" style={{ color: accent }} />
                  <div>
                    <div className="text-[13px] font-semibold" style={{ color: t.ink }}>We never ask for a seed phrase or a password</div>
                    <p className="text-[12.5px] leading-relaxed mt-1" style={{ color: t.ink2 }}>
                      Not by email, not by phone, not on this page. Funding details are confirmed with you directly after your request is reviewed.
                    </p>
                  </div>
                </div>
              </div>

              {/* application */}
              <form ref={formRef} onSubmit={submit} className="rounded-3xl p-6 md:p-7 space-y-4"
                style={{ background: t.cardStrong, border: `1px solid ${t.border}`, boxShadow: `0 24px 60px ${alpha(accent, 0.1)}` }} data-testid="portal-form">
                <div>
                  <h2 className="font-display text-xl font-bold tracking-tight" style={{ color: t.ink }}>{cta}</h2>
                  <p className="text-[12.5px] mt-1" style={{ color: t.ink3 }}>Takes a minute. No money moves at this stage.</p>
                </div>
                {offer && (
                  <div className="rounded-2xl px-4 py-3 flex items-center justify-between gap-3" style={{ background: alpha(accent, 0.09), border: `1px solid ${alpha(accent, 0.28)}` }}>
                    <div className="min-w-0">
                      <div className={LABEL} style={{ color: t.ink3 }}>Selected</div>
                      <div className="font-display font-semibold truncate" style={{ color: t.ink }}>{offer.name}</div>
                    </div>
                    <div className="font-display text-xl font-extrabold tabular-nums shrink-0" style={{ color: accent }}>{offer.promised_rate_pct}%</div>
                  </div>
                )}
                {(portal.fields || []).map((f) => (
                  <label key={f.key} className="block">
                    <span className={LABEL} style={{ color: t.ink3 }}>{f.label}{f.required && " *"}</span>
                    {f.type === "textarea" ? (
                      <textarea value={values[f.key] || ""} required={f.required} rows={3}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                        className="w-full mt-1.5 px-3.5 py-2.5 rounded-xl text-sm outline-none focus:ring-2"
                        style={{ ...fieldStyle, "--tw-ring-color": alpha(accent, 0.4) }}
                        data-testid={`portal-field-${f.key}`} />
                    ) : (
                      <input type={f.type === "number" ? "number" : f.type === "email" ? "email" : f.type === "tel" ? "tel" : "text"}
                        value={values[f.key] || ""} required={f.required}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                        className="w-full mt-1.5 px-3.5 py-2.5 rounded-xl text-sm outline-none focus:ring-2"
                        style={{ ...fieldStyle, "--tw-ring-color": alpha(accent, 0.4) }}
                        data-testid={`portal-field-${f.key}`} />
                    )}
                  </label>
                ))}
                <label className="block">
                  <span className={LABEL} style={{ color: t.ink3 }}>Amount (USDT) *</span>
                  <input value={amount} onChange={(e) => setAmount(e.target.value)} required inputMode="decimal"
                    placeholder={offer ? `${offer.min_ticket}–${offer.max_ticket}` : ""}
                    className="w-full mt-1.5 px-3.5 py-2.5 rounded-xl text-sm tabular-nums outline-none focus:ring-2"
                    style={{ ...fieldStyle, "--tw-ring-color": alpha(accent, 0.4) }}
                    data-testid="portal-amount" />
                  {offer && (
                    <span className="block text-[11px] mt-1.5" style={{ color: t.ink3 }}>
                      This plan takes {usd(offer.min_ticket)}–{usd(offer.max_ticket)}.
                    </span>
                  )}
                </label>
                {error && (
                  <div className="rounded-xl px-3.5 py-2.5 text-[12.5px] font-medium" style={{ background: "rgba(225,29,72,0.1)", color: "#E11D48", border: "1px solid rgba(225,29,72,0.25)" }} data-testid="portal-error">
                    {error}
                  </div>
                )}
                <button type="submit" disabled={busy || !offerId}
                  className="w-full rounded-full py-3.5 text-[14px] font-bold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                  style={{ background: accent, color: onAccent, boxShadow: `0 14px 32px ${alpha(accent, 0.32)}` }} data-testid="portal-submit">
                  {busy ? "Sending…" : <>{cta} <ArrowRight size={15} /></>}
                </button>
                <p className="text-[11px] leading-relaxed" style={{ color: t.ink3 }}>
                  Submitting this doesn't move any money. {portal.name} reviews your request and confirms the next step by email.
                </p>
              </form>
            </div>
          </div>
        )}

        <About portal={portal} t={t} accent={accent} />
        <HowItWorks t={t} accent={accent} name={portal.name} />
        <Footer portal={portal} t={t} accent={accent} />
      </div>
    </div>
  );
}
